/* eslint-disable react-hooks/exhaustive-deps */
//#region Libraries
import { useState, useEffect } from "react";
//#endregion
//#region Styles
import { Container, SpaceHeader } from "./styles";
import { InactiveActivity } from "views/components/Schedules/styles";
//#endregion
//#region Components
import Schedules from "views/components/Schedules/Schedules";
import CustomButton from "components/CustomButton/CustomButton";
import SchedulesSkelletonContent from "views/components/Schedules/components/SchedulesSkelletonContent/SchedulesSkelletonContent";
import ScheduleFreeActivity from "../ScheduleFreeActivity";
//#endregion
//#region Icons
import EditIcon from "icons/EditIcon";
//#endregion
//#region Utils
import { QUANTITY_PER_PAGE } from "views/components/Schedules/utils/constants";
import { isPastSchedule } from "utils/date";
import useShowDialogAlert from "components/DialogAlert/utils/hooks/useDialogAlert";
import useResourcesUniversitySpacesContext from "../utils/context/useResourcesUniversitySpacesContext";
import RegularReservationActivity from "../RegularReservationActivity";
import AdminReservationActivity from "../AdminReservationActivity";
//#endregion

const SchedulesWrapper = ({
    data, openShowDialogSpaceForm,
    isLoading, openDialogNonBusinessDay,
    appliedFilters
}) => {
    const dialogConfirmExitWithoutSaving = useShowDialogAlert();
    const { spaceList } = useResourcesUniversitySpacesContext();
    //#region States
    const [schedules, setSchedules] = useState({
        headers: [],
        body: [],
        cellsManyHours: []
    });
    const [hoursToIgnored, setHoursToIgnored] = useState(Array(7).fill([]));
    //#endregion
    //#region Effects
    useEffect(() => {
        fillData();
    }, [spaceList.value]);
    //#endregion
    //#region Functions
    const fillData = () => {
        if (!spaceList.value) return;
        const offsetColumns = Array(QUANTITY_PER_PAGE - spaceList.value.length).fill(null);
        const spaceListWithOffsetColumns = spaceList.value.concat(offsetColumns);
        setSchedules({
            ...schedules,
            headers: spaceListWithOffsetColumns.map(getHeaderData),
            body: spaceListWithOffsetColumns.map(getBodyData),
        });
        fillHoursToIgnored();
    }
    const fillHoursToIgnored = () => {
        const newHoursToIgnored = spaceList.value.flatMap(({ space, reservations, adminReservations }) => {
            return {
                spaceId: space.id,
                // Se ordena para facilitar la búsqueda de reservas cercanas en función de las horas
                hours: [
                    ...reservations.map(({ schedule: { timeStart, timeEnd } }) => ({
                        start: timeStart,
                        end: timeEnd
                    })),
                    ...adminReservations.map(({ schedule: { timeStart, timeEnd } }) => ({
                        start: timeStart,
                        end: timeEnd
                    }))
                    // Ordenando las horas del más antiguo al más reciente
                ].sort((a, b) => a.start - b.start)
            };
        });
        setHoursToIgnored(newHoursToIgnored);
    }
    const getContentHeaderData = space => {
        if (!space)
            return <span></span>;
        const { name } = space;
        return (
            <>
                <span title={name}>{name}</span>
                <CustomButton
                    icon={<EditIcon />}
                    variant="terciary"
                    width="30px"
                    height="32px"
                    onClick={() => {
                        openShowDialogSpaceForm({
                            spaceTypeId: null, space
                        });
                    }} />
            </>
        );
    }
    const getHeaderData = spaceInfo => {
        const classList = ["main-content"];
        const space = spaceInfo?.space;
        if (space && !space.active)
            classList.push("inactive");
        return {
            render: (
                <SpaceHeader className={classList.join(" ")}>
                    {getContentHeaderData(space)}
                </SpaceHeader>
            )
        };
    }
    const getBodyData = spaceItem => {
        if (!spaceItem) return null;
        const {
            space, reservations, adminReservations
        } = spaceItem;
        const spaceSimplified = {
            id: space.id,
            name: space.name,
            reference: space.reference,
            minCapacity: space.minCapacity,
            maxCapacity: space.maxCapacity,
            details: space.details,
        };
        return {
            ...spaceItem,
            reservations: reservations.map(reservation => {
                const { campus, address, spaceType} = reservation;
                return {
                    ...reservation,
                    space: {
                        ...spaceSimplified,
                        campus, address, spaceType
                    }
                };
            }),
            adminReservations: adminReservations.map(adminReservation => {
                const { campus, address, spaceType} = adminReservation;
                return {
                    ...adminReservation,
                    space: {
                        ...spaceSimplified,
                        campus, address, spaceType
                    }
                };
            })
        };
    }
    const isPartOfReservationOfManyHours = (space, time) => {
        const foundHourIgnored = hoursToIgnored.find(({ spaceId }) => spaceId === space.id);
        return foundHourIgnored?.hours.some(hourItem => time.totalMinutes > hourItem.start && time.totalMinutes < hourItem.end) || false;
    }
    const getQuantityCellsOfEmptyScheduleActivity = ({
        space, time,
        isHourOclock
    }) => {
        const hoursToIgnoredOfSpace = hoursToIgnored
            .find(({ spaceId }) => spaceId === space.id)
            .hours;
        const prevHourReservation = hoursToIgnoredOfSpace
            .find(hourItem => time.totalMinutes === hourItem.start - (hourItem.start % 60));
        const postHourReservation = hoursToIgnoredOfSpace
            .find(hourItem => time.totalMinutes === hourItem.end);
        // Verificando hora anterior de la ocupada para rellenar el espacio
        if (prevHourReservation) {
            // Obteniendo la cantidad de celdas que posiblemente se tomarán a partir del horario de reserva previo al tiempo actual
            const posibleQuantityCells = (prevHourReservation.start - time.totalMinutes) / 15;
            // Obtiendo tiempo que debe ser ignorado si es que su hora de finalización es la hora de inicio del horario de reserva previo al tiempo actual
            const foundHourToIgnoredOfResource = hoursToIgnoredOfSpace.find(({ end }) => prevHourReservation.start === end);
            if (foundHourToIgnoredOfResource) {
                // Se obtiene el tiempo que abarca este horario 
                const diff = foundHourToIgnoredOfResource.end - foundHourToIgnoredOfResource.start;
                // Si la diferencia es menor o igual a 30, entonces se decide retornar la posibl cantidad de celdas menos el tiempo que abarca el horario obtenido dividido entre 15, valor equivalente a 1 celda en la grilla de horarios
                if (diff <= 30)
                    return posibleQuantityCells - (diff / 15)
            }
            // Obteniendo ahora el horario posterior al previo para corroborar si es solo de 15 minutos
            const postClosestHourToIgnore = hoursToIgnoredOfSpace.find(({ start }) => prevHourReservation.start > start);
            if (postClosestHourToIgnore && prevHourReservation.start - postClosestHourToIgnore.end === 15)
                return posibleQuantityCells - 2;
            // Devolviendo la cantidad de celdas posibles si ninguna condición fue verificada
            return posibleQuantityCells;
        }
        // Verificando hora posterior de la ocupada para rellenar el espacio
        if (postHourReservation) {
            let rows = 4 - (postHourReservation.end % 60) / 15;
            // Ajustando filas tomadas por si existe una intervalos ocupados posterior a la hora posterior
            const totalMinutesToOccupied = postHourReservation.end + 15 * rows;
            const reservationCross = hoursToIgnoredOfSpace.find(hourItem => hourItem.start >= postHourReservation.end && hourItem.start <= totalMinutesToOccupied);
            if (reservationCross) {
                const offset = (totalMinutesToOccupied - reservationCross.start) / 15;
                rows -= offset;
            }
            return rows;
        }
        // Verificando si no es una hora en punto
        if (!isHourOclock)
            return null;
        // Por defecto retornando el tamaño de hora
        return 4;
    }
    const renderInactivityActivity = (idx, quantityCells = 4) => {
        return (
            <td key={idx} rowSpan={quantityCells}>
                <InactiveActivity heightRatio={quantityCells} />
            </td>
        );
    }
    function findReservation(reservations, currentMinutes) {
        return reservations.find(({ schedule }) => schedule.timeStart === currentMinutes);
    }
    function findAdminReservation(adminReservations, currentMinutes) {
        return adminReservations.find(({ schedule }) => schedule.timeStart === currentMinutes);
    }
    function spaceIsInactiveAndHasNotReservations({ space, reservations, adminReservations }) {
        return !space.active && reservations.length === 0 && adminReservations.length === 0;
    }
    const renderActivity = ({
        cell: {
            time, item, idx
        },
        helpers: {
            renderEmptyActivity
        }
    }) => {
        const emptyActivityColor = idx % 2 === 0 ? "light" : "gray";
        const isHourOclock = time.minutes === 0;
        if (!item)
            return isHourOclock && renderEmptyActivity(idx, emptyActivityColor);
        const {
            space, reservations, adminReservations
        } = item;
        const isPast = isPastSchedule({
            date: appliedFilters.value.date,
            minutes: time.totalMinutes
        }) ;
        if (!isPast && spaceIsInactiveAndHasNotReservations(item) && isHourOclock)
            return renderInactivityActivity(idx);
        const currentMinutes = time.totalMinutes;
        const reservationFound = findReservation(reservations, currentMinutes);
        if (reservationFound) 
            return (
                <RegularReservationActivity
                    key={idx}
                    data={data}
                    openConfirmExitWithoutSavingDialog={dialogConfirmExitWithoutSaving.open}
                    reservation={reservationFound}
                    space={space}/>
            );
        const adminReservationFound = findAdminReservation(adminReservations, currentMinutes);
        if (adminReservationFound) 
            return (
                <AdminReservationActivity
                    key={idx}
                    openConfirmExitWithoutSavingDialog={dialogConfirmExitWithoutSaving.open}
                    adminReservation={adminReservationFound}/>
            );
        if (isPartOfReservationOfManyHours(space, time))
            return null;
        const quantityCells = getQuantityCellsOfEmptyScheduleActivity({
            time, isHourOclock, space
        });
        if (!quantityCells)
            return null;
        if (!space.active) 
            return renderInactivityActivity(idx, quantityCells);
        if (isPast) 
            return renderEmptyActivity(idx, emptyActivityColor, quantityCells);
        return (
            <ScheduleFreeActivity
                key={idx}
                data={data}
                space={space}
                isNonBusinessDay={item.isNonBusinessDay}
                filterDate={appliedFilters.value.date}
                quantityCells={quantityCells}
                activityColor={emptyActivityColor} 
                openDialogNonBusinessDay={openDialogNonBusinessDay}/>
        );
    }
    //#endregion
    return (
        <Container>
            {isLoading
                ? <SchedulesSkelletonContent />
                : <Schedules
                    data={schedules}
                    filterDate={appliedFilters.value.date}
                    renderActivity={renderActivity} />}
        </Container>
    );
}

export default SchedulesWrapper;